<template lang="pug">
control
  field(:class="{ invalid: !plaintext.meta.valid && plaintext.meta.touched }")
    label(:for="plaintext.id")(v-html="plaintext.label")
    div
      input(
        @focus="plaintext.focus"
        @blur="plaintext.blur"
        v-if="plaintext.mask"
        v-mask="plaintext.mask"
        v-model="plaintext.model",
        :type="plaintext.hidden ? (visible ? 'text' : 'password') : 'text'",
        :name="plaintext.id",
        :id="plaintext.id",
        :maxlength="plaintext.max"
        :placeholder="plaintext.placeholder",
        autocomplete="off")
      input(
        @blur="plaintext.blur"
        v-else
        v-model="plaintext.model",
        :type="plaintext.hidden ? (visible ? 'text' : 'password') : 'text'",
        :name="plaintext.id",
        :id="plaintext.id",
        :maxlength="plaintext.max"
        :placeholder="plaintext.placeholder",
        autocomplete="off")
      button(
        v-if="plaintext.hidden",
        @click.stop.prevent="visible = !visible",
        @keypress.enter.stop.prevent="visible = !visible",
        type="button")
        i.far(:class="visible ? 'fa-eye-slash' : 'fa-eye'")

    error(v-if="!plaintext.meta.valid && plaintext.meta.touched") {{ plaintext.errorMessage }}
</template>

<script>
import { useStore } from 'vuex';
import { reactive, computed, ref, watch } from 'vue';
import { useField } from 'vee-validate';
import * as yup from 'yup';
export default {
  name: 'PlainText',
  props: {
    field: Object,
  },
  setup(p) {
    const store = useStore();
    const visible = ref(false);
    const plaintext = reactive(initPlaintext());

    return { visible, plaintext };

    function initPlaintext() {

      let labelHtml = p.field.label;
      if (labelHtml && p.field.required !== false) labelHtml = `<b>${labelHtml}</b>`;

      const id = p.field.id;
      const label = labelHtml ? `${labelHtml}${p.field.required !== false ? " <span class='required'>*</span>" : ""}` : null;
      const hidden = p.field.hidden;
      const mask = p.field.mask || false;
      const max = p.field.max || false;

      let rules = ref();
      const isSSN = id.includes('ssn');

      const makeRules = () => {
        rules.value = yup.string().trim();
        if (p.field.required) {
          rules.value = rules.value.required();
        }
        if (max) {
          rules.value = rules.value.max(max);
        }
        if (p.field.regex) {
          rules.value = rules.value.matches(new RegExp(p.field.regex[0]), p.field.regex[1])
        }
        if (isSSN) {
          let otherSSNRegex = Object.entries(store.getters.model).filter(([key]) => {
            return key.includes('ssn') && key !== id;
          }).reduce((reg, [_, ssn], i, arr) => {
            if (i === 0) {
              reg = "^(?!(?:";
            }
            reg += ssn.replaceAll('-', '\\-');
            if (i < arr.length - 1) {
              reg += '|';
            }
            else {
              reg += ')$).*$';
            }
            return reg;
          }, false);

          if (otherSSNRegex) {
            rules.value = rules.value.matches(new RegExp(otherSSNRegex), "Cannot reuse SSN");
          }
        }
      }

      makeRules();

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      if (isSSN) {
        const notifySSNUpdated = computed(() => store.getters.notifySSNUpdated);
        watch(notifySSNUpdated, () => {
          if (store.getters.lastSSNUpdatedID !== id) {
            makeRules();
            validate();
          }
        });
      }

      const model = computed({
        get: () => store.getters.model[id],
        set: v => {
          value.value = v;
          store.commit('updateModel', { id, value: v });
        },
      });

      const focus = () => {
        makeRules();
      }

      const blur = async () => {
        store.commit('pushLastFieldToDataLayer', id);
        await validate();
        handleBlur();
      };

      const placeholder = p.field.required === false
        ? ""
        : "(Required)";


      return { id, label, hidden, mask, max, model, meta, blur, focus, errorMessage, placeholder };
    }
  },
};
</script>

<style lang="scss" scoped>
div {
  @include toggleWrapper;
}

button {
  @include toggleIcon;
}
</style>
